import React, { useRef, useEffect } from "react";
import { useMap } from "../hooks/incontext/useMap";
import { Control } from "ol/control";
import { CompassIcon } from "../constants/icons/CompassIcon";
import { useCompassRotation } from "../hooks/useCompassRotation";
import { truncateNumber } from "../../../utils/truncateNumber";

export type Size = "sm" | "md" | "lg";

/**
 * 컴퍼스 회전 휠 컨트롤 props — 지도 회전을 시각적으로 제어.
 *
 * `resetable` true 시 리셋 버튼 노출, `controller` true 시 각도 직접 입력 컨트롤 노출.
 */
export interface CompassWheelProps {
  /** 휠 크기 토큰: sm=72px / md=125px / lg=200px */
  size?: Size;
  onWheel?: (degree: number) => void;
  /** 0도 리셋 버튼 노출 (기본 false) */
  resetable?: boolean;
  /** 각도 입력 input 컨트롤 노출 (기본 false) */
  controller?: boolean;
}

const COMPASS_ICON_SIZE: Record<Size, number> = { sm: 72, md: 125, lg: 200 };

export const CompassWheel = ({
  size = "sm",
  onWheel,
  resetable = false,
  controller = false,
}: CompassWheelProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const map = useMap();

  const {
    rotation,
    setRotation,
    setRotate,
    applyRotationToMap,
    handleMouseDown,
    resetValue,
  } = useCompassRotation(map, { onWheel });

  // OL 커스텀 컨트롤로 DOM 요소 등록
  useEffect(() => {
    const control = new Control({ element: ref.current ?? undefined });
    map.addControl(control);
    return () => { map.removeControl(control); };
  }, [map]);

  const iconSize = COMPASS_ICON_SIZE[size];

  return (
    <div ref={ref} style={{ position: "absolute", top: "10px", right: "10px" }}>
      <div
        onDoubleClick={resetValue}
        onMouseDown={handleMouseDown}
        style={{
          zIndex: 1,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: "center center",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CompassIcon draggable={false} height={iconSize} width={iconSize} />
        </div>
      </div>
      {resetable && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
          <button
            style={{
              width: "60px",
              height: "20px",
              background: "white",
              border: "1px solid #858484",
              borderRadius: "3px",
              paddingBottom: "3px",
              color: "black",
            }}
            onClick={resetValue}
          >
            reset
          </button>
        </div>
      )}
      {controller && (
        <div style={{ marginTop: "px", width: "100%", display: "flex", justifyContent: "center" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setRotate(rotation);
              applyRotationToMap(rotation);
            }}
          >
            <input
              value={truncateNumber(rotation, 0)}
              onChange={(e) => {
                const next = Number(e.target.value);
                setRotation(next);
                setRotate(next);
                applyRotationToMap(next);
              }}
              type="number"
              name="degree"
              style={{ width: "53px", color: "black" }}
            />
          </form>
        </div>
      )}
    </div>
  );
};
